import type { ChipProps } from '@mui/material';
import { Chip, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { formatNumber, isNotNullOrUndefined } from '@vy/utils';
import React from 'react';
import { colors } from '../../assets/themes/colors';

type PercentageChipProps = {
  oldValue: number;
  newValue: number;
  postfix?: string;
  prefix?: string;
  withBackground?: boolean;
  bold?: boolean;
  arrowIconSize?: number;
} & ChipProps;

export const PercentageChip: React.FC<PercentageChipProps> = ({
  oldValue,
  newValue,
  postfix,
  prefix,
  withBackground = false,
  bold = true,
  arrowIconSize = 24,
  sx,
  ...props
}) => {
  const percentageDifference = getPercentageDifference(oldValue, newValue);
  const isPositiveDifference = percentageDifference >= 0;
  const determinedColor = isPositiveDifference ? '#43A047' : colors.red[100];

  return (
    <Chip
      variant="outlined"
      size="small"
      sx={{
        backgroundColor: getChipBackgroundColor(withBackground, isPositiveDifference),
        '&:hover': {
          boxShadow: 'none',
        },
        ...sx,
      }}
      icon={
        <VyIcon
          icon={isPositiveDifference ? 'ArrowTopRight' : 'ArrowBottomRight'}
          color={determinedColor}
          width={arrowIconSize}
          height={arrowIconSize}
          viewBox="0 0 24 24"
        />
      }
      label={
        <Typography
          sx={{
            fontSize: "13px",
            lineHeight: "18px",
            fontWeight: bold ? 600 : 400,
            minWidth: '5ch',
            textAlign: 'end',
            color: determinedColor
          }}>
          {isNotNullOrUndefined(prefix) && (
            <Typography
              component="span"
              sx={{
                fontWeight: "inherit",
                fontSize: "12px"
              }}>
              {prefix}
            </Typography>
          )}

          {` ${formatNumber(Math.abs(percentageDifference))}% `}

          {isNotNullOrUndefined(postfix) && (
            <Typography
              component="span"
              sx={{
                fontWeight: "inherit",
                fontSize: "12px"
              }}>
              {postfix}
            </Typography>
          )}
        </Typography>
      }
      {...props}
    />
  );
};

function getPercentageDifference(oldValue: number, newValue: number) {
  if (oldValue === 0 && newValue === 0) {
    return 0;
  }

  if (oldValue === 0) {
    return 100;
  }

  return ((newValue - oldValue) / oldValue) * 100;
}

function getChipBackgroundColor(withBackground: boolean, isPositiveDifference: boolean) {
  if (!withBackground) {
    return 'transparent';
  }

  return isPositiveDifference ? '#E8F5E9' : '#FFE3E3';
}
